import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import { Link as RouterLink, useHistory } from "react-router-dom";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import { downloadAuthenticatedFile } from "network/download-authenticated-file";
import {
  leagueCustomerMembershipSettingsRoute,
  leagueCustomerMigrationPlayersPreviewRoute,
  leagueCustomerMigrationPlayersTemplateRoute,
  leagueCustomerMigrationStatusRoute,
  leagueCustomerMigrationTeamsPreviewRoute,
  leagueCustomerMigrationTeamsTemplateRoute,
  leagueCustomerMigrationExecuteRoute,
} from "network/api-routes";
import { GESTION_ROUTES } from "constants/gestion-routes";
import { useGestionCustomer } from "hooks/useGestionCustomer";
import GestionMembershipSettings from "components/pages/Gestion/GestionMembershipSettings";
import GestionFansburyMigration from "components/pages/Gestion/GestionFansburyMigration";
import GestionTable from "components/pages/Gestion/GestionTable";
import { isRevaAdminUser } from "utils/membership-ui-helpers";

const STEPS = ["settings", "players", "teams", "summary"];

const GestionMigrationWizard = () => {
  const intl = useIntl();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useContext(UserContext);
  const { customerId } = useGestionCustomer();
  const isRevaAdmin = isRevaAdminUser(user);

  const [activeStep, setActiveStep] = useState(0);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [amountsConfigured, setAmountsConfigured] = useState(false);
  const [monthlyBilling, setMonthlyBilling] = useState(false);

  const [playersFile, setPlayersFile] = useState(null);
  const [playersPreview, setPlayersPreview] = useState(null);
  const [playersLoading, setPlayersLoading] = useState(false);

  const [teamsFile, setTeamsFile] = useState(null);
  const [teamsPreview, setTeamsPreview] = useState(null);
  const [teamsLoading, setTeamsLoading] = useState(false);

  const [destructive, setDestructive] = useState(false);
  const [destructiveDialogOpen, setDestructiveDialogOpen] = useState(false);
  const [csvRun, setCsvRun] = useState(null);
  const [rowLimit, setRowLimit] = useState(2000);
  const [executeLoading, setExecuteLoading] = useState(false);
  // Which current previews were already sent to execute. A new preview re-enables "Ejecutar
  // importación" even when the latest run is done (G7-1: fix errors and re-import, or import
  // teams after players) without sending the already-imported file again.
  const [submitted, setSubmitted] = useState({ players: false, teams: false });

  const stepLabels = useMemo(
    () => STEPS.map((step) => intl.formatMessage({ id: `gestion.migration.step.${step}` })),
    [intl],
  );

  const loadSettingsGate = useCallback(() => {
    if (!customerId) return;
    setSettingsLoading(true);
    performRequest("get", leagueCustomerMembershipSettingsRoute(customerId), {}, true, user)
      .then((res) => {
        const monthly = Number(res.data?.membership_monthly_amount);
        setAmountsConfigured(Number.isFinite(monthly) && monthly > 0);
        setMonthlyBilling(Number.isFinite(monthly) && monthly > 0);
      })
      .catch((error) => {
        enqueueSnackbar(error?.response?.data?.message || intl.formatMessage({ id: "gestion.migration.error.settings" }), {
          variant: "error",
        });
      })
      .finally(() => setSettingsLoading(false));
  }, [customerId, enqueueSnackbar, intl, user]);

  useEffect(() => {
    if (!isRevaAdmin && customerId) {
      history.replace(GESTION_ROUTES.dashboard(customerId));
    }
  }, [customerId, history, isRevaAdmin]);

  useEffect(() => {
    if (isRevaAdmin) {
      loadSettingsGate();
    }
  }, [isRevaAdmin, loadSettingsGate]);

  const loadCsvStatus = useCallback(() => {
    if (!customerId) return Promise.resolve();
    return performRequest("get", leagueCustomerMigrationStatusRoute(customerId), {}, true, user).then((res) => {
      setCsvRun(res.data?.run || null);
      if (res.data?.row_limit) setRowLimit(res.data.row_limit);
      return res.data;
    });
  }, [customerId, user]);

  const csvActive = csvRun?.status === "queued" || csvRun?.status === "running";

  useEffect(() => {
    if (!isRevaAdmin) return undefined;
    loadCsvStatus().catch(() => {});
    return undefined;
  }, [isRevaAdmin, loadCsvStatus]);

  useEffect(() => {
    if (!customerId || !csvActive) return undefined;
    const timer = setInterval(() => {
      loadCsvStatus().catch(() => {});
    }, 3000);
    return () => clearInterval(timer);
  }, [csvActive, customerId, loadCsvStatus]);

  useEffect(() => {
    if (csvActive) setActiveStep(3);
  }, [csvActive]);

  // A failed run may be retried with the same files, as before.
  useEffect(() => {
    if (csvRun?.status === "failed") setSubmitted({ players: false, teams: false });
  }, [csvRun?.status]);

  const downloadTemplate = (kind) => {
    const route =
      kind === "players"
        ? leagueCustomerMigrationPlayersTemplateRoute(customerId)
        : leagueCustomerMigrationTeamsTemplateRoute(customerId);
    downloadAuthenticatedFile({
      url: route,
      user,
      filename: kind === "players" ? "migration-players-template.csv" : "migration-teams-template.csv",
      accept: "text/csv",
    }).catch(() => enqueueSnackbar(intl.formatMessage({ id: "gestion.migration.error.template" }), { variant: "error" }));
  };

  const previewErrorMessage = (error) => {
    const message = error?.response?.data?.message || "";
    if (String(message).includes("row limit")) {
      return intl.formatMessage({ id: "gestion.migration.error.rowLimit" }, { limit: rowLimit });
    }
    return message || intl.formatMessage({ id: "gestion.migration.error.preview" });
  };

  const previewPlayers = () => {
    if (!playersFile) return;
    const formData = new FormData();
    formData.append("file", playersFile);
    setPlayersLoading(true);
    performRequest("post", leagueCustomerMigrationPlayersPreviewRoute(customerId), formData, true, user, null, {
      "Content-Type": "multipart/form-data",
    })
      .then((res) => {
        setPlayersPreview(res.data);
        setSubmitted((prev) => ({ ...prev, players: false }));
      })
      .catch((error) =>
        enqueueSnackbar(previewErrorMessage(error), {
          variant: "error",
        }),
      )
      .finally(() => setPlayersLoading(false));
  };

  const previewTeams = () => {
    if (!teamsFile) return;
    const formData = new FormData();
    formData.append("file", teamsFile);
    // The players of this run are created before the teams: let the preview know them (G7-4).
    if (playersFile && !submitted.players) formData.append("players", playersFile);
    setTeamsLoading(true);
    performRequest("post", leagueCustomerMigrationTeamsPreviewRoute(customerId), formData, true, user, null, {
      "Content-Type": "multipart/form-data",
    })
      .then((res) => {
        setTeamsPreview(res.data);
        setSubmitted((prev) => ({ ...prev, teams: false }));
      })
      .catch((error) =>
        enqueueSnackbar(previewErrorMessage(error), {
          variant: "error",
        }),
      )
      .finally(() => setTeamsLoading(false));
  };

  const handlePlayersFileChange = (event) => {
    const file = event.target.files?.[0];
    setPlayersFile(file || null);
    setPlayersPreview(null);
    setTeamsPreview(null);
    event.target.value = "";
  };

  const handleTeamsFileChange = (event) => {
    const file = event.target.files?.[0];
    setTeamsFile(file || null);
    setTeamsPreview(null);
    event.target.value = "";
  };

  const canContinueFromSettings = amountsConfigured && monthlyBilling && !settingsLoading;
  const playersReady = (playersPreview?.summary?.valid_rows || 0) > 0;
  const teamsReady = (teamsPreview?.summary?.valid_rows || 0) > 0;
  const playersPending = playersReady && !submitted.players;
  const teamsPending = teamsReady && !submitted.teams;
  const canExecute = (playersPending || teamsPending) && !csvActive && !executeLoading;

  const handleNext = async () => {
    if (activeStep === 1 && playersFile && !playersPreview) {
      enqueueSnackbar(intl.formatMessage({ id: "gestion.migration.error.previewFirst" }), { variant: "warning" });
      return;
    }
    if (activeStep === 2 && teamsFile && !teamsPreview) {
      enqueueSnackbar(intl.formatMessage({ id: "gestion.migration.error.previewFirst" }), { variant: "warning" });
      return;
    }
    if (activeStep === 0) {
      setSettingsLoading(true);
      try {
        const res = await performRequest("get", leagueCustomerMembershipSettingsRoute(customerId), {}, true, user);
        const monthly = Number(res.data?.membership_monthly_amount);
        const ok = Number.isFinite(monthly) && monthly > 0;
        setAmountsConfigured(ok);
        setMonthlyBilling(ok);
        if (!ok) {
          enqueueSnackbar(intl.formatMessage({ id: "gestion.migration.settings.blocked" }), { variant: "warning" });
          return;
        }
      } catch (error) {
        enqueueSnackbar(error?.response?.data?.message || intl.formatMessage({ id: "gestion.migration.error.settings" }), {
          variant: "error",
        });
        return;
      } finally {
        setSettingsLoading(false);
      }
    }
    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 0));

  const executeImport = () => {
    if (!canExecute) return;
    const formData = new FormData();
    if (playersFile && playersPending) formData.append("players", playersFile);
    if (teamsFile && teamsPending) formData.append("teams", teamsFile);
    formData.append("destructive", destructive ? "1" : "0");
    setExecuteLoading(true);
    performRequest("post", leagueCustomerMigrationExecuteRoute(customerId), formData, true, user, null, {
      "Content-Type": "multipart/form-data",
    })
      .then((res) => {
        setCsvRun(res.data?.run || null);
        setSubmitted((prev) => ({ players: prev.players || playersPending, teams: prev.teams || teamsPending }));
        enqueueSnackbar(intl.formatMessage({ id: "gestion.migration.execute.started" }), { variant: "info" });
      })
      .catch((error) =>
        enqueueSnackbar(previewErrorMessage(error) || intl.formatMessage({ id: "gestion.migration.error.execute" }), {
          variant: "error",
        }),
      )
      .finally(() => {
        setExecuteLoading(false);
        setDestructiveDialogOpen(false);
      });
  };

  const requestExecute = () => {
    if (destructive && playersPending) {
      setDestructiveDialogOpen(true);
      return;
    }
    executeImport();
  };

  const renderPreviewTable = (preview, kind) => {
    if (!preview?.rows?.length) {
      return (
        <RevaText color="text.secondary">
          {intl.formatMessage({ id: "gestion.migration.preview.empty" })}
        </RevaText>
      );
    }

    return (
      <Box sx={{ overflowX: "auto" }}>
        <GestionTable
          headers={[
            { key: "row", title: intl.formatMessage({ id: "gestion.migration.table.row" }) },
            { key: "action", title: intl.formatMessage({ id: "gestion.migration.table.action" }) },
            {
              key: "label",
              title: intl.formatMessage({
                id: kind === "players" ? "gestion.migration.table.player" : "gestion.migration.table.team",
              }),
            },
            { key: "status", title: intl.formatMessage({ id: "gestion.migration.table.status" }) },
          ]}
          rows={preview.rows.map((row) => {
            const label =
              kind === "players"
                ? `${row.data?.first_name || ""} ${row.data?.last_name || ""} (${row.data?.document_number || ""})`
                : row.data?.team_name;
            const status =
              row.errors?.length > 0
                ? row.errors.join(" ")
                : [
                    row.warnings?.length ? row.warnings.join(" ") : null,
                    kind === "players" && row.dues_preview
                      ? intl.formatMessage(
                          { id: "gestion.migration.dues.summary" },
                          {
                            paid: row.dues_preview.paid_count,
                            pending: row.dues_preview.pending_count,
                          },
                        )
                      : kind === "teams" && row.will_assign
                        ? intl.formatMessage({ id: "gestion.migration.teams.willAssign" })
                        : intl.formatMessage({ id: "gestion.migration.row.ok" }),
                  ]
                    .filter(Boolean)
                    .join(" · ");

            return {
              id: `${kind}-${row.row}`,
              row: row.row,
              action: row.action,
              label,
              status,
            };
          })}
        />
      </Box>
    );
  };

  const renderStepContent = () => {
    if (activeStep === 0) {
      return (
        <Stack spacing={2}>
          <RevaText>{intl.formatMessage({ id: "gestion.migration.settings.intro" })}</RevaText>
          {!settingsLoading && !canContinueFromSettings && (
            <Alert severity="warning">{intl.formatMessage({ id: "gestion.migration.settings.blocked" })}</Alert>
          )}
          <GestionMembershipSettings embedded />
        </Stack>
      );
    }

    if (activeStep === 1) {
      return (
        <Stack spacing={2}>
          <RevaText>{intl.formatMessage({ id: "gestion.migration.players.intro" }, { limit: rowLimit })}</RevaText>
          <Alert severity="info">{intl.formatMessage({ id: "gestion.migration.rowLimit" }, { limit: rowLimit })}</Alert>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => downloadTemplate("players")}>
              {intl.formatMessage({ id: "gestion.migration.downloadTemplate" })}
            </Button>
            <input accept=".csv" id="migration-players-csv" type="file" hidden onChange={handlePlayersFileChange} />
            <label htmlFor="migration-players-csv">
              <Button variant="contained" component="span" startIcon={<UploadFileIcon />}>
                {intl.formatMessage({ id: "gestion.migration.selectCsv" })}
              </Button>
            </label>
            {playersFile && <RevaText>{playersFile.name}</RevaText>}
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button variant="outlined" disabled={!playersFile || playersLoading} onClick={previewPlayers}>
              {intl.formatMessage({ id: "gestion.migration.preview" })}
            </Button>
            <FormControlLabel
              control={<Checkbox checked={destructive} onChange={(e) => setDestructive(e.target.checked)} />}
              label={intl.formatMessage({ id: "gestion.migration.destructive" })}
            />
          </Stack>
          {playersLoading && <CircularProgress size={28} />}
          {playersPreview && (
            <Paper sx={{ p: 2 }}>
              <RevaText variant="subtitle1" sx={{ mb: 1 }}>
                {intl.formatMessage(
                  { id: "gestion.migration.preview.summary" },
                  {
                    valid: playersPreview.summary?.valid_rows || 0,
                    errors: playersPreview.summary?.error_rows || 0,
                  },
                )}
              </RevaText>
              {renderPreviewTable(playersPreview, "players")}
            </Paper>
          )}
        </Stack>
      );
    }

    if (activeStep === 2) {
      return (
        <Stack spacing={2}>
          <RevaText>{intl.formatMessage({ id: "gestion.migration.teams.intro" }, { limit: rowLimit })}</RevaText>
          <Alert severity="info">{intl.formatMessage({ id: "gestion.migration.rowLimit" }, { limit: rowLimit })}</Alert>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => downloadTemplate("teams")}>
              {intl.formatMessage({ id: "gestion.migration.downloadTemplate" })}
            </Button>
            <input accept=".csv" id="migration-teams-csv" type="file" hidden onChange={handleTeamsFileChange} />
            <label htmlFor="migration-teams-csv">
              <Button variant="contained" component="span" startIcon={<UploadFileIcon />}>
                {intl.formatMessage({ id: "gestion.migration.selectCsv" })}
              </Button>
            </label>
            {teamsFile && <RevaText>{teamsFile.name}</RevaText>}
          </Stack>
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" disabled={!teamsFile || teamsLoading} onClick={previewTeams}>
              {intl.formatMessage({ id: "gestion.migration.preview" })}
            </Button>
          </Stack>
          {teamsLoading && <CircularProgress size={28} />}
          {teamsPreview && (
            <Paper sx={{ p: 2 }}>
              <RevaText variant="subtitle1" sx={{ mb: 1 }}>
                {intl.formatMessage(
                  { id: "gestion.migration.preview.summary" },
                  {
                    valid: teamsPreview.summary?.valid_rows || 0,
                    errors: teamsPreview.summary?.error_rows || 0,
                  },
                )}
              </RevaText>
              {renderPreviewTable(teamsPreview, "teams")}
            </Paper>
          )}
        </Stack>
      );
    }

    const playersSummary = csvRun?.summary?.players;
    const teamsSummary = csvRun?.summary?.teams;
    // The latest run may come from an earlier visit; its result stays visible, but only the
    // run started from these previews makes the step "finished".
    const finished = csvRun?.status === "done";
    const finishedThisImport = finished && (submitted.players || submitted.teams) && !playersPending && !teamsPending;

    return (
      <Stack spacing={2}>
        <RevaText variant="h6">
          {intl.formatMessage({
            id: finishedThisImport ? "gestion.migration.summary.title" : "gestion.migration.summary.readyTitle",
          })}
        </RevaText>
        <RevaText>
          {intl.formatMessage({
            id: finishedThisImport ? "gestion.migration.summary.body" : "gestion.migration.summary.readyBody",
          })}
        </RevaText>
        <Alert severity={playersPreview && !playersReady ? "warning" : "info"}>
          {playersPreview
            ? intl.formatMessage(
                { id: "gestion.migration.summary.players" },
                {
                  valid: playersPreview.summary?.valid_rows || 0,
                  errors: playersPreview.summary?.error_rows || 0,
                  create: playersPreview.summary?.would_create_members || 0,
                },
              )
            : intl.formatMessage({ id: "gestion.migration.summary.playersSkipped" })}
        </Alert>
        <Alert severity={teamsPreview && !teamsReady ? "warning" : "info"}>
          {teamsPreview
            ? intl.formatMessage(
                { id: "gestion.migration.summary.teams" },
                {
                  valid: teamsPreview.summary?.valid_rows || 0,
                  errors: teamsPreview.summary?.error_rows || 0,
                  create: teamsPreview.summary?.would_create_teams || 0,
                  assignments: teamsPreview.summary?.would_assign_players || 0,
                },
              )
            : intl.formatMessage({ id: "gestion.migration.summary.teamsSkipped" })}
        </Alert>
        {csvRun?.status === "queued" && (
          <Alert severity="info">{intl.formatMessage({ id: "gestion.migration.execute.started" })}</Alert>
        )}
        {csvRun?.status === "running" && (
          <Alert severity="info">
            {intl.formatMessage(
              { id: "gestion.migration.running" },
              {
                step: intl.formatMessage({
                  id: `gestion.migration.step.${csvRun.current_step || "summary"}`,
                }),
              },
            )}
          </Alert>
        )}
        {csvRun?.status === "failed" && (
          <Alert severity="error">
            {intl.formatMessage({ id: "gestion.migration.failed" }, { error: csvRun.error_message || "" })}
          </Alert>
        )}
        {finished && playersSummary && (
          <Alert severity="success">
            {intl.formatMessage(
              { id: "gestion.migration.players.result" },
              {
                processed: playersSummary.processed || 0,
                created: playersSummary.created_members || 0,
                paid: playersSummary.installments_paid || 0,
              },
            )}
          </Alert>
        )}
        {finished && teamsSummary && (
          <Alert severity="success">
            {intl.formatMessage(
              { id: "gestion.migration.teams.result" },
              {
                processed: teamsSummary.processed || 0,
                teams: teamsSummary.teams_created || 0,
                assignments: teamsSummary.assignments || 0,
              },
            )}
          </Alert>
        )}
        {!finishedThisImport && (
          <Button variant="contained" disabled={!canExecute} onClick={requestExecute}>
            {executeLoading ? <CircularProgress size={22} color="inherit" /> : intl.formatMessage({ id: "gestion.migration.execute" })}
          </Button>
        )}
        {finished && (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button component={RouterLink} to={GESTION_ROUTES.players(customerId)} variant="contained">
              {intl.formatMessage({ id: "gestion.nav.players" })}
            </Button>
            <Button component={RouterLink} to={GESTION_ROUTES.financials(customerId)} variant="outlined">
              {intl.formatMessage({ id: "gestion.nav.financials" })}
            </Button>
          </Stack>
        )}
      </Stack>
    );
  };

  if (!isRevaAdmin) {
    return null;
  }

  return (
    <Box sx={{ maxWidth: "100%", overflowX: "hidden" }}>
      <RevaText variant="h5" sx={{ mb: 2 }}>
        {intl.formatMessage({ id: "gestion.migration.title" })}
      </RevaText>
      <GestionFansburyMigration />
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <RevaText variant="subtitle1">{intl.formatMessage({ id: "gestion.migration.csv.title" })}</RevaText>
        </AccordionSummary>
        <AccordionDetails>
      <RevaText sx={{ mb: 2 }}>{intl.formatMessage({ id: "gestion.migration.csv.intro" })}</RevaText>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
        {stepLabels.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {renderStepContent()}

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 4 }}>
        <Button disabled={activeStep === 0} onClick={handleBack}>
          {intl.formatMessage({ id: "gestion.migration.back" })}
        </Button>
        <Button variant="contained" disabled={activeStep === STEPS.length - 1} onClick={handleNext}>
          {intl.formatMessage({ id: "gestion.migration.next" })}
        </Button>
      </Stack>
        </AccordionDetails>
      </Accordion>

      <Dialog open={destructiveDialogOpen} onClose={() => setDestructiveDialogOpen(false)}>
        <DialogTitle>{intl.formatMessage({ id: "gestion.migration.destructive.title" })}</DialogTitle>
        <DialogContent>
          <DialogContentText>{intl.formatMessage({ id: "gestion.migration.destructive.body" })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDestructiveDialogOpen(false)}>
            {intl.formatMessage({ id: "gestion.migration.destructive.cancel" })}
          </Button>
          <Button color="warning" variant="contained" onClick={executeImport} disabled={executeLoading}>
            {intl.formatMessage({ id: "gestion.migration.destructive.confirm" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GestionMigrationWizard;
